import axios from 'axios'
import toast from 'react-hot-toast'
import { API_URL } from './constants'

// Type augmentation: permite pasar skipErrorToast en config para suprimir
// el toast del interceptor (útil en polling y endpoints donde 404 es normal).
declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface AxiosRequestConfig {
    skipErrorToast?: boolean
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface InternalAxiosRequestConfig {
    skipErrorToast?: boolean
  }
}

// Base URL viene de lib/constants.ts (única fuente de verdad).
// En dev, Vite proxy redirige /api → backend; en prod, /api está en el mismo dominio.

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for session cookies (HttpOnly)
})

// Request interceptor (no-op). Auth viaja automáticamente en cookie HttpOnly
// gracias a withCredentials: true. NO leer tokens de localStorage.
api.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
)

// Response interceptor.
//
// Patrón: si el caller no quiere que el interceptor muestre toast (caso típico:
// polling, GETs opcionales como /chat/historial cuando aún no hay historial,
// búsquedas que pueden retornar 404, etc.), pasar `{ skipErrorToast: true }`
// en el config. También se silencian automáticamente los GET con 404 — un GET
// que retorna 404 suele significar "el recurso no existe (esperado)" en vez de
// "algo fatal". Para POST/PUT/DELETE sí se muestra (el caller pidió algo).
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const skipErrorToast = error.config?.skipErrorToast === true

    if (error.response) {
      const { status, data } = error.response
      const method = String(error.config?.method || '').toUpperCase()

      switch (status) {
        case 401:
          // Unauthorized - redirect to login (siempre)
          if (window.location.pathname !== '/login') {
            toast.error('Sesión expirada. Por favor inicia sesión nuevamente.')
            window.location.href = '/login'
          }
          break
        case 403:
          if (!skipErrorToast) toast.error('No tienes permisos para realizar esta acción.')
          break
        case 404:
          // Silenciar 404 en GETs (esperados); mostrar en mutaciones (inesperados)
          if (!skipErrorToast && method !== 'GET') {
            toast.error('Recurso no encontrado.')
          }
          break
        case 422:
          if (!skipErrorToast) toast.error(data.message || 'Error de validación.')
          break
        case 429:
          if (!skipErrorToast) toast.error('Demasiadas solicitudes. Intenta de nuevo en unos minutos.')
          break
        case 500:
          if (!skipErrorToast) toast.error('Error del servidor. Por favor intenta más tarde.')
          break
        default:
          if (!skipErrorToast) toast.error(data.message || 'Ocurrió un error inesperado.')
      }
    } else if (error.request) {
      if (!skipErrorToast) toast.error('No se pudo conectar con el servidor.')
    } else {
      if (!skipErrorToast) toast.error('Error al procesar la solicitud.')
    }

    return Promise.reject(error)
  }
)

export default api
